/**
 * Signature capture component using signature_pad.
 * Renders a canvas with Clear / Done controls.
 * Returns a base64 PNG data URL on completion.
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import SignaturePadLib from 'signature_pad';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Eraser, Check, PenLine } from 'lucide-react';

interface SignaturePadProps {
  label?: string;
  /** Called with base64 PNG data URL when user taps Done / or null when cleared */
  onChange: (dataUrl: string | null) => void;
  /** Pre-filled data URL to display */
  value?: string | null;
  width?: number;
  height?: number;
  disabled?: boolean;
}

export function SignaturePad({
  label = 'Signature',
  onChange,
  value,
  width = 400,
  height = 150,
  disabled,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [captured, setCaptured] = useState(!!value);

  // Initialise signature_pad
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    // Scale canvas for hi-DPI
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
    });

    pad.addEventListener('endStroke', () => {
      setIsEmpty(pad.isEmpty());
    });

    padRef.current = pad;

    // Restore from value
    if (value) {
      pad.fromDataURL(value);
      setIsEmpty(false);
      setCaptured(true);
    }

    if (disabled) pad.off();

    return () => {
      pad.off();
    };
  }, [width, height, disabled]);

  const clear = useCallback(() => {
    padRef.current?.clear();
    setIsEmpty(true);
    setCaptured(false);
    onChange(null);
  }, [onChange]);

  const confirm = useCallback(() => {
    if (!padRef.current || padRef.current.isEmpty()) return;
    const dataUrl = padRef.current.toDataURL('image/png');
    setCaptured(true);
    onChange(dataUrl);
  }, [onChange]);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-1.5">
        <PenLine className="h-3 w-3" />
        {label}
      </Label>
      <div className="border rounded-md overflow-hidden inline-block bg-white">
        <canvas
          ref={canvasRef}
          style={{ width, height, touchAction: 'none' }}
          className={disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}
        />
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={clear}
            className="text-xs h-7 gap-1"
          >
            <Eraser className="h-3 w-3" /> Clear
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={confirm}
            disabled={isEmpty}
            className="text-xs h-7 gap-1"
          >
            <Check className="h-3 w-3" /> {captured ? 'Update' : 'Capture'}
          </Button>
          {captured && (
            <span className="text-xs text-green-600 self-center ml-1">Signature captured</span>
          )}
        </div>
      )}
    </div>
  );
}
